import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Snackbar,
  Alert,
  useTheme,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import PermissionGroupService from "../../services/tenantServices/PermissionGroupService";
import PermissionMatrix from "../../components/PermissionMatrix";
import TenantLayout from "../../layouts/TenantLayout";
import SecurityIcon from "@mui/icons-material/Security";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { tenantPanelContentSx } from "./tenantPanelContentSx";

export default function CreatePermissionGroup() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams(); // Get group ID from URL for edit mode
  const permissionService = new PermissionGroupService();

  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    type: "admin",
    name: "",
    label: "",
    description: "",
  });

  const [permissions, setPermissions] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch group data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchGroupData();
    }
  }, [id]);

  const fetchGroupData = async () => {
    try {
      setPageLoading(true);
      const group = await permissionService.getGroupById(id);
      
      // Set basic form data
      setFormData({
        type: group.type,
        name: group.name,
        label: group.label,
        description: group.description || "",
      });

      // Extract permissions (all can_* fields)
      const permissionData = {};
      Object.keys(group).forEach(key => {
        if (key.startsWith('can_')) {
          permissionData[key] = group[key];
        }
      });
      setPermissions(permissionData);
    } catch (error) {
      console.error("Error fetching group data:", error);
      setSnackbar({
        open: true,
        message: "Error loading group data",
        severity: "error",
      });
      setTimeout(() => {
        navigate("/tenant/permission-groups");
      }, 2000);
    } finally {
      setPageLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Group name is required";
    } else if (formData.name.length < 3) {
      newErrors.name = "Group name must be at least 3 characters";
    }

    if (!formData.label.trim()) {
      newErrors.label = "Display label is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the form errors",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      
      // Default permissions for admin type groups
      const defaultPermissions = {
        can_own_permission_groups_create: true,
        can_own_permission_groups_add: true,
        can_own_permission_groups_remove: true,
        can_own_permission_groups_view: true,
        can_own_permission_groups_update: true,
      };
      
      const groupData = {
        ...formData,
        ...defaultPermissions,
        ...permissions, // User-selected permissions will override defaults if needed
      };

      if (isEditMode) {
        await permissionService.updateGroup(id, groupData);
        setSnackbar({
          open: true,
          message: "Permission group updated successfully!",
          severity: "success",
        });
      } else {
        await permissionService.createGroup(groupData);
        setSnackbar({
          open: true,
          message: "Permission group created successfully!",
          severity: "success",
        });
      }

      setTimeout(() => {
        navigate("/tenant/permission-groups");
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 
        (isEditMode ? "Error updating permission group" : "Error creating permission group");
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (newType) => {
    setFormData({ ...formData, type: newType });
    setPermissions({});
  };

  const selectedPermissionsCount = Object.values(permissions).filter(v => v === true).length;

  // Show loading state while fetching data in edit mode
  if (pageLoading) {
    return (
      <TenantLayout contentSx={tenantPanelContentSx}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={50} sx={{ color: colors.blueAccent[500], mb: 2 }} />
            <Typography variant="h5" sx={{ color: "#64748b" }}>
              Loading group data...
            </Typography>
          </Box>
        </Box>
      </TenantLayout>
    );
  }

  return (
    <TenantLayout contentSx={tenantPanelContentSx}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate("/tenant/permission-groups")}
          sx={{
            backgroundColor: `${colors.grey[700]}40`,
            "&:hover": {
              backgroundColor: `${colors.grey[600]}60`,
            },
          }}
        >
          <ArrowBackIcon sx={{ color: colors.grey[300] }} />
        </IconButton>
        
        <SecurityIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, #1e293b 0%, ${colors.blueAccent[600]} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {isEditMode ? "Edit Permission Group" : "Create Permission Group"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            {isEditMode 
              ? "Update access control rules for this group"
              : "Define access control rules for your team members"}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            border: `1px solid ${colors.grey[800]}30`,
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.grey[100],
              fontWeight: 700,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📝 Basic Information
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Group Type - Fixed to Admin */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: `${colors.blueAccent[800]}20`,
                border: `1px solid ${colors.blueAccent[500]}40`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: colors.grey[300], mb: 1, fontWeight: 600 }}
              >
                Group Type
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AdminPanelSettingsIcon sx={{ fontSize: 22, color: "white" }} />
                </Box>
                <Typography sx={{ color: colors.grey[100], fontWeight: 700, fontSize: "1.1rem" }}>
                  Admin
                </Typography>
              </Box>
            </Box>

            {/* Group Name */}
            <TextField
              fullWidth
              label="Group Name (Internal)"
              placeholder="e.g., LIMITED_ADMIN, READ_ONLY_DOCTOR"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/\s/g, '_') })}
              error={!!errors.name}
              helperText={
                isEditMode 
                  ? "Group name cannot be changed after creation"
                  : (errors.name || "Unique identifier for this group (uppercase, underscores)")
              }
              required
              disabled={isEditMode}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: isEditMode ? colors.grey[700] : colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />

            {/* Display Label */}
            <TextField
              fullWidth
              label="Display Label"
              placeholder="e.g., Limited Admin, Read-Only Doctor"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              error={!!errors.label}
              helperText={errors.label || "User-friendly name shown in the interface"}
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />

            {/* Description */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              placeholder="Describe the purpose and scope of this permission group..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.primary[600],
                  "& fieldset": {
                    borderColor: `${colors.grey[800]}40`,
                  },
                  "&:hover fieldset": {
                    borderColor: colors.blueAccent[500],
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.greenAccent[500],
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: colors.grey[100],
                },
                "& .MuiInputLabel-root": {
                  color: colors.grey[300],
                },
                "& .MuiFormHelperText-root": {
                  color: colors.grey[400],
                },
              }}
            />
          </Box>
        </Paper>

        {/* Permission Matrix */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                color: "#1e293b",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              🔐 Permissions Configuration
            </Typography>
            <Chip
              label={`${selectedPermissionsCount} permission${selectedPermissionsCount !== 1 ? 's' : ''} selected`}
              sx={{
                  backgroundColor: selectedPermissionsCount > 0 
                  ? `${colors.greenAccent[100]}`
                  : `${colors.grey[200]}`,
                color: selectedPermissionsCount > 0 
                  ? colors.greenAccent[700]
                  : "#64748b",
                fontWeight: 600,
                border: `1px solid ${selectedPermissionsCount > 0 
                  ? colors.greenAccent[500] 
                  : colors.grey[700]}40`,
              }}
            />
          </Box>

          <PermissionMatrix
            type={formData.type}
            permissions={permissions}
            onChange={setPermissions}
          />
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/tenant/permission-groups")}
            disabled={loading}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: "#cbd5e1",
              color: "#475569",
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
            sx={{
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}40`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 6px 16px ${colors.greenAccent[500]}60`,
              },
              "&:disabled": {
                background: colors.grey[600],
                color: colors.grey[400],
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading 
              ? (isEditMode ? "Updating..." : "Creating...") 
              : (isEditMode ? "Update Permission Group" : "Create Permission Group")}
          </Button>
        </Box>
      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </TenantLayout>
  );
}

